import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useToast } from '../../context/ToastContext';

const EyeOpen = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOff = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);
const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);
const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
  </svg>
);

export default function LoginPage() {
  const { login } = useCustomerAuth();
  const toast     = useToast();
  const navigate  = useNavigate();

  const [phone,    setPhone]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [focused,  setFocused]  = useState('');
  const [theme,    setTheme]    = useState(() => localStorage.getItem('wt-theme') || 'dark');
  const isLight = theme === 'light';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('wt-theme', theme);
  }, [theme]);

  const submit = async e => {
    e.preventDefault();
    if (!phone.trim() || !password) return;
    setLoading(true);
    try {
      await login(phone.trim(), password);
      navigate('/customer/home', { replace: true });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const C = {
    pageBg:       isLight ? 'linear-gradient(145deg,#dbeafe 0%,#bae6fd 50%,#e0f2fe 100%)'
                           : 'linear-gradient(145deg,#010c18 0%,#03111e 55%,#051828 100%)',
    cardBg:       isLight ? 'rgba(255,255,255,0.75)' : 'rgba(4,18,36,0.75)',
    cardBorder:   isLight ? 'rgba(10,186,181,0.22)'  : 'rgba(10,186,181,0.18)',
    cardShadow:   isLight ? '0 24px 60px rgba(0,0,0,0.10)' : '0 24px 60px rgba(0,0,0,0.45)',
    inputBg:      isLight ? 'rgba(240,249,255,0.85)' : 'rgba(1,12,26,0.65)',
    textPrimary:  isLight ? '#0c2340'                : 'rgba(240,249,255,0.95)',
    textMuted:    isLight ? '#3d7ca0'                : 'rgba(186,230,253,0.48)',
    toggleBg:     isLight ? 'rgba(0,0,0,0.07)'       : 'rgba(255,255,255,0.09)',
    toggleBorder: isLight ? 'rgba(0,0,0,0.1)'        : 'rgba(255,255,255,0.14)',
    toggleColor:  isLight ? '#0c2340'                : 'rgba(240,249,255,0.8)',
  };
  const inputBorder = (name) => focused === name
    ? '1.5px solid rgba(10,186,181,0.75)'
    : `1.5px solid ${isLight ? 'rgba(10,186,181,0.18)' : 'rgba(10,186,181,0.12)'}`;
  const inputShadow = (name) => focused === name ? '0 0 0 3px rgba(10,186,181,0.13)' : 'none';

  return (
    <div dir="rtl" style={{
      minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      padding:'1rem', background: C.pageBg, fontFamily:"'Cairo',sans-serif",
      position:'relative', overflow:'hidden',
    }}>
      <style>{`
        @keyframes wt-blob { 0%,100%{transform:translate(0,0) scale(1)} 40%{transform:translate(28px,-18px) scale(1.06)} 70%{transform:translate(-16px,14px) scale(0.96)} }
        @keyframes wt-in   { from{opacity:0;transform:translateY(22px) scale(0.96)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes wt-logo  { from{opacity:0;transform:translateY(-16px) scale(0.88)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes wt-spin  { to{transform:rotate(360deg)} }
        .wt-login-input { outline:none; width:100%; box-sizing:border-box; }
        .wt-login-input::placeholder { opacity:0.45; }
      `}</style>

      {/* Blobs */}
      <div style={{ position:'absolute', width:400, height:400, borderRadius:'50%', top:'-12%', right:'-12%',
        background: isLight ? 'rgba(10,186,181,0.13)' : 'rgba(10,186,181,0.08)',
        filter:'blur(55px)', animation:'wt-blob 10s ease-in-out infinite', pointerEvents:'none' }}/>
      <div style={{ position:'absolute', width:280, height:280, borderRadius:'50%', bottom:'-8%', left:'-8%',
        background: isLight ? 'rgba(56,189,248,0.14)' : 'rgba(2,80,140,0.1)',
        filter:'blur(45px)', animation:'wt-blob 13s ease-in-out infinite reverse', pointerEvents:'none' }}/>

      {/* Theme Toggle */}
      <button onClick={() => setTheme(t => t==='dark'?'light':'dark')} style={{
        position:'absolute', top:'1rem', left:'1rem',
        width:36, height:36, borderRadius:'50%', cursor:'pointer',
        background: C.toggleBg, border:`1px solid ${C.toggleBorder}`,
        backdropFilter:'blur(12px)', display:'flex', alignItems:'center', justifyContent:'center',
        color: C.toggleColor,
      }}>
        {isLight ? <MoonIcon/> : <SunIcon/>}
      </button>

      <div style={{ width:'100%', maxWidth:'22rem', animation:'wt-in 0.4s cubic-bezier(0.34,1.2,0.64,1)' }}>

        {/* Logo block */}
        <div style={{ textAlign:'center', marginBottom:'1.75rem', animation:'wt-logo 0.5s cubic-bezier(0.34,1.4,0.64,1)' }}>
          <img
            src={isLight ? '/assets/images/logo/blue-logo.png' : '/assets/images/logo/white-logo.png'}
            alt="WTRLY"
            style={{ height:60, width:'auto', objectFit:'contain', marginBottom:'0.9rem', display:'block', margin:'0 auto 0.9rem' }}
            onError={e => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.insertAdjacentHTML('afterend',
                `<svg width="48" height="48" viewBox="0 0 24 24" fill="${isLight?'#0ABAB5':'rgba(240,249,255,0.9)'}" style="margin-bottom:0.9rem"><path d="M12 2c-5.33 4.55-8 8.48-8 11.8 0 4.98 3.8 8.2 8 8.2s8-3.22 8-8.2c0-3.32-2.67-7.25-8-11.8z"/></svg>`
              );
            }}
          />
          <div style={{ fontSize:'1.7rem', fontWeight:900, letterSpacing:'-0.01em', color: C.textPrimary, lineHeight:1.05 }}>
            WTRLY
          </div>
          <div style={{ fontSize:'0.78rem', fontWeight:600, color: C.textMuted, marginTop:'0.25rem' }}>
            بوابة العملاء
          </div>
        </div>

        {/* Card */}
        <div style={{
          borderRadius:'1.25rem', overflow:'hidden',
          background: C.cardBg,
          backdropFilter:'blur(32px) saturate(200%)', WebkitBackdropFilter:'blur(32px) saturate(200%)',
          border:`1px solid ${C.cardBorder}`,
          boxShadow:`${C.cardShadow}, 0 0 0 1px rgba(10,186,181,0.07)`,
        }}>
          <div style={{ height:3, background:'linear-gradient(90deg,transparent,#0ABAB5 40%,rgba(56,189,248,0.8),transparent)' }}/>

          <div style={{ padding:'1.5rem' }}>
            {/* Back button + badge */}
            <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'1.2rem' }}>
              <a href="/login" style={{
                width:34, height:34, borderRadius:'50%',
                background: isLight ? 'rgba(10,186,181,0.1)' : 'rgba(255,255,255,0.09)',
                border:`1px solid ${isLight ? 'rgba(10,186,181,0.25)' : 'rgba(255,255,255,0.14)'}`,
                display:'flex', alignItems:'center', justifyContent:'center',
                color: C.textMuted, textDecoration:'none', flexShrink:0,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
              </a>
              <div style={{
                padding:'0.3rem 0.9rem', borderRadius:'2rem',
                background:'linear-gradient(135deg,#0ABAB5,#0891b2)',
                color:'#fff', fontSize:'0.82rem', fontWeight:800,
              }}>عميل</div>
            </div>

            <h2 style={{ margin:'0 0 1.2rem', fontSize:'1.05rem', fontWeight:800, color: C.textPrimary }}>
              تسجيل الدخول
            </h2>

            <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:'0.85rem' }}>

              {/* Phone */}
              <div>
                <label style={{ display:'block', fontSize:'0.72rem', fontWeight:700, color:C.textMuted, marginBottom:'0.38rem' }}>
                  رقم الهاتف
                </label>
                <div style={{ position:'relative' }}>
                  <span style={{
                    position:'absolute', right:'0.8rem', top:'50%', transform:'translateY(-50%)',
                    color: focused==='phone' ? '#0ABAB5' : C.textMuted, display:'flex', pointerEvents:'none',
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.08 1.18 2 2 0 012.06 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/>
                    </svg>
                  </span>
                  <input
                    className="wt-login-input"
                    type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                    onFocus={() => setFocused('phone')} onBlur={() => setFocused('')}
                    placeholder="05xxxxxxxx" required dir="ltr"
                    style={{
                      padding:'0.72rem 2.4rem 0.72rem 0.85rem',
                      borderRadius:'0.75rem', fontSize:'0.875rem', fontWeight:600,
                      background: C.inputBg, border: inputBorder('phone'), color: C.textPrimary,
                      boxShadow: inputShadow('phone'), transition:'border 0.2s,box-shadow 0.2s',
                      fontFamily:"'Cairo',sans-serif",
                    }}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={{ display:'block', fontSize:'0.72rem', fontWeight:700, color:C.textMuted, marginBottom:'0.38rem' }}>
                  كلمة المرور
                </label>
                <div style={{ position:'relative' }}>
                  <span style={{
                    position:'absolute', right:'0.8rem', top:'50%', transform:'translateY(-50%)',
                    color: focused==='pw' ? '#0ABAB5' : C.textMuted, display:'flex', pointerEvents:'none',
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0110 0v4"/>
                    </svg>
                  </span>
                  <input
                    className="wt-login-input"
                    type={showPw ? 'text' : 'password'}
                    value={password} onChange={e => setPassword(e.target.value)}
                    onFocus={() => setFocused('pw')} onBlur={() => setFocused('')}
                    placeholder="••••••••" required
                    style={{
                      padding:'0.72rem 2.4rem 0.72rem 2.4rem',
                      borderRadius:'0.75rem', fontSize:'0.875rem',
                      background: C.inputBg, border: inputBorder('pw'), color: C.textPrimary,
                      boxShadow: inputShadow('pw'), transition:'border 0.2s,box-shadow 0.2s',
                      fontFamily:"'Cairo',sans-serif",
                    }}
                  />
                  <button type="button" onClick={() => setShowPw(p => !p)} style={{
                    position:'absolute', left:'0.7rem', top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', cursor:'pointer', padding:'0.2rem',
                    color: showPw ? '#0ABAB5' : C.textMuted, display:'flex',
                  }}>
                    {showPw ? <EyeOff/> : <EyeOpen/>}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit" disabled={loading}
                style={{
                  marginTop:'0.25rem', width:'100%', padding:'0.85rem',
                  borderRadius:'0.875rem', border:'none', cursor: loading ? 'not-allowed' : 'pointer',
                  background:'linear-gradient(135deg,#0ABAB5,#0891b2)',
                  color:'#fff', fontSize:'0.9rem', fontWeight:800,
                  boxShadow:'0 6px 24px rgba(10,186,181,0.38)',
                  opacity: loading ? 0.7 : 1,
                  transition:'all 0.2s ease',
                  fontFamily:"'Cairo',sans-serif",
                  display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem',
                }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.filter='brightness(1.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.filter=''; }}
              >
                {loading ? (
                  <>
                    <span style={{
                      width:16, height:16, borderRadius:'50%',
                      border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff',
                      animation:'wt-spin 0.7s linear infinite', display:'inline-block',
                    }}/>
                    جاري الدخول...
                  </>
                ) : 'دخول'}
              </button>
            </form>

            {/* Divider */}
            <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', margin:'1.1rem 0 0.9rem' }}>
              <div style={{ flex:1, height:1, background: isLight ? 'rgba(10,186,181,0.12)' : 'rgba(56,189,248,0.1)' }}/>
              <span style={{ fontSize:'0.58rem', fontWeight:800, letterSpacing:'0.16em', color:C.textMuted, userSelect:'none' }}>
                WTRLY
              </span>
              <div style={{ flex:1, height:1, background: isLight ? 'rgba(10,186,181,0.12)' : 'rgba(56,189,248,0.1)' }}/>
            </div>

            <div style={{ textAlign:'center' }}>
              <span style={{ fontSize:'0.78rem', color:C.textMuted }}>ليس لديك حساب؟ </span>
              <Link to="/customer/register" style={{
                fontSize:'0.78rem', fontWeight:800, color:'#0ABAB5', textDecoration:'none',
              }}>
                سجّل الآن
              </Link>
            </div>
          </div>
        </div>

        <p style={{ textAlign:'center', color:C.textMuted, fontSize:'0.68rem', marginTop:'1.25rem', opacity:0.7 }}>
          © {new Date().getFullYear()} WTRLY — جميع الحقوق محفوظة
        </p>
      </div>
    </div>
  );
}
